import React, { useState, useEffect, useRef } from "react";
import { CloudDownload, Terminal, CheckCircle2, Server, RefreshCw, ShieldCheck, FileText, Cpu } from "lucide-react";
import { REACT_APP_API_URL, APP_CLIENT_NAME } from "config/api-config";
import axios from "axios";

export default function OTAUpdatePanel() {
  const [checking, setChecking] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [logs, setLogs] = useState([]);
  const [updateComplete, setUpdateComplete] = useState(false);
  
  const terminalEndRef = useRef(null);

  // Auto-scroll terminal
  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  // 1. Ask FastAPI what needs updating
  const checkForUpdates = async () => {
    setChecking(true);
    setLogs(["Connecting to CamsecAI Update Server...", "Validating component timestamps..."]);
    
    try {
      // Utilizing the globally configured client name
      const response = await axios.get(`${REACT_APP_API_URL}/system/check_updates?client_name=${APP_CLIENT_NAME}`);
      
      if (response.data.update_available) {
        setUpdateData(response.data);
        setLogs(prev => [...prev, ...response.data.components_to_update.map(c => `[Found Update]: ${c}`), "System ready for upgrade."]);
      } else {
        setLogs(prev => [...prev, "All CamSec Ai components are up to date."]);
      }
    } catch (error) {
      setLogs(prev => [...prev, "ERROR: Failed to reach Master Server.", error.message]);
    } finally {
      setChecking(false);
    }
  };

  // 2. Trigger Bash script and stream live logs
  const startUpdate = async () => {
    if (!window.confirm("WARNING: The system services will temporarily restart. Proceed?")) return;
    
    setUpdating(true);
    setLogs(["Initiating secure OTA Update stream..."]);

    try {
      // Use standard fetch() to handle the ReadableStream response
      const response = await fetch(`${REACT_APP_API_URL}/system/install_update?client_name=${APP_CLIENT_NAME}`, {
        method: 'POST',
      });

      // Catch HTTP errors that fetch() silently ignores
      if (!response.ok) throw new Error(`Server responded with status ${response.status}`);
      if (!response.body) throw new Error("ReadableStream not supported in this browser.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        // Decode with { stream: true } to handle chunks properly
        const textChunk = decoder.decode(value, { stream: true });
        
        // Split by newlines and add to log state
        const newLines = textChunk.split('\n').filter(line => line.trim() !== '');
        if (newLines.length > 0) {
          setLogs(prev => [...prev, ...newLines]);
        }
      }
      
      setUpdating(false);
      setUpdateComplete(true);
      
    } catch (error) {
      setLogs(prev => [...prev, `CRITICAL ERROR: ${error.message}`]);
      setUpdating(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 25px rgba(0,0,0,0.05)", maxWidth: "800px", margin: "0 auto" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "30px", borderBottom: "1px solid #F4F7FE", paddingBottom: "20px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: "#F0EBFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Server size={28} color="#4318FF" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#2B3674", fontSize: "22px", fontWeight: "700" }}>System Updates</h3>
          <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Manage CamSec Ai infrastructure and patch management.</p>
        </div>
      </div>

      {/* STATUS & ACTIONS */}
      {!updating && !updateComplete && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#F8FAFC", padding: "24px", borderRadius: "16px", border: "1px solid #E2E8F0", marginBottom: "20px" }}>
          <div>
            <h4 style={{ margin: "0 0 8px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>System Status</h4>
            <p style={{ margin: 0, color: "#707EAE", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldCheck size={16} color="#00E396" /> Security patches active.
            </p>
          </div>
          
          <button 
            onClick={checkForUpdates} 
            disabled={checking}
            style={{ padding: "12px 24px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700", cursor: checking ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px" }}
          >
            <RefreshCw size={18} className={checking ? "animate-spin" : ""} />
            {checking ? "Checking Master..." : "Check for Updates"}
          </button>
        </div>
      )}

      {/* RELEASE NOTES & INSTALL BUTTON */}
      {updateData && !updating && !updateComplete && (
        <div style={{ marginBottom: "25px", animation: "fadeIn 0.3s" }}>
          <div style={{ backgroundColor: "#fff8f1", border: "1px solid #ffe4cc", borderRadius: "16px", padding: "20px", marginBottom: "15px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#FF912C", fontSize: "16px", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" }}>
              <FileText size={18} /> Release Notes
            </h4>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#475467", fontSize: "14px", fontFamily: "inherit", lineHeight: "1.5" }}>
              {updateData.release_notes}
            </pre>
            
            <div style={{ marginTop: "15px", paddingTop: "15px", borderTop: "1px dashed #ffe4cc" }}>
              <h5 style={{ margin: "0 0 8px 0", color: "#2B3674", fontSize: "14px" }}>Components pending upgrade:</h5>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {updateData.components_to_update.map((comp, idx) => (
                  <span key={idx} style={{ backgroundColor: "white", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", color: "#4318FF", border: "1px solid #f0ebff", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Cpu size={14} /> {comp}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          <button 
            onClick={startUpdate}
            style={{ width: "100%", padding: "14px", backgroundColor: "#05CD99", color: "white", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxShadow: "0px 4px 15px rgba(5, 205, 153, 0.3)" }}
          >
            <CloudDownload size={20} />
            Install System Update Now
          </button>
        </div>
      )}

      {/* LIVE TERMINAL LOGS */}
      <div style={{ backgroundColor: "#0B1437", borderRadius: "16px", padding: "20px", border: "1px solid #1B254B", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid #2B3674", paddingBottom: "12px", marginBottom: "12px" }}>
          <Terminal size={16} color="#A3AED0" />
          <span style={{ color: "#A3AED0", fontSize: "12px", fontWeight: "700", letterSpacing: "1px" }}>LIVE DEPLOYMENT LOGS</span>
        </div>
        
        <div style={{ height: "250px", overflowY: "auto", fontFamily: "monospace", fontSize: "13px", color: "#00E396", display: "flex", flexDirection: "column", gap: "6px" }}>
          {logs.length === 0 ? (
            <span style={{ color: "#707EAE" }}>Awaiting commands...</span>
          ) : (
            logs.map((log, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", color: log.includes("ERROR") || log.includes("[-]") ? "#EE5D50" : log.includes("[+]") ? "#4318FF" : "#00E396" }}>
                <span style={{ color: "#4318FF", flexShrink: 0 }}>{">"}</span>
                <span style={{ wordBreak: "break-all" }}>{log}</span>
              </div>
            ))
          )}
          <div ref={terminalEndRef} />
        </div>
      </div>

      {/* POST-UPDATE INSTRUCTIONS */}
      {updateComplete && (
        <div style={{ marginTop: "20px", backgroundColor: "#f0ebff", border: "1px solid #d4c4ff", borderRadius: "16px", padding: "24px", display: "flex", gap: "16px", alignItems: "flex-start", animation: "fadeIn 0.5s" }}>
          <CheckCircle2 size={24} color="#4318FF" style={{ flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: "0 0 8px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>Upgrade Successful</h4>
            <p style={{ margin: "0 0 12px 0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              The infrastructure has been successfully updated. The backend services may take up to 2 minutes to fully initialize and connect to the AI accelerators.
            </p>
            <p style={{ margin: 0, color: "#EE5D50", fontSize: "14px", fontWeight: "700" }}>
              Please wait 2 minutes, then perform a HARD RELOAD (Ctrl + Shift + R) to clear your browser cache.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
